import React, { useState, useEffect } from 'react';
import { X, CreditCard, QrCode, Ticket, ShieldCheck, Loader2, CheckCircle2, Check, Zap } from 'lucide-react';
import { db, auth } from "../firebase";
import { doc, setDoc, getDoc, arrayUnion } from "firebase/firestore"; // Added getDoc
import emailjs from '@emailjs/browser'; 
import { validatePromo, calculateDiscountedPrice } from '../utils/promoHelper';
import { PLAN_CONFIG } from '../config/planConfig';

const CheckoutModal = ({ 
  isOpen, 
  onClose, 
  planPrice = PLAN_CONFIG.profitable.basePrice, 
  planName = PLAN_CONFIG.profitable.name 
}) => {
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("UPI"); 
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [userHistory, setUserHistory] = useState([]); // State to store fetched history

  useEffect(() => {
    emailjs.init("hse-Ri4J-pOKOYoLm");

    // Fetch used promos from Firestore when the modal opens
    const fetchUserHistory = async () => {
      const user = auth.currentUser;
      if (user && isOpen) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            setUserHistory(userDoc.data().usedPromos || []);
          }
        } catch (error) {
          console.error("Error fetching user history:", error);
        }
      }
    };

    fetchUserHistory();
  }, [isOpen]);

  const finalPrice = calculateDiscountedPrice(planPrice, appliedPromo);

  const handleApplyPromo = () => {
    // Pass userHistory to the validator to prevent double-use
    const result = validatePromo(promoCode, userHistory);
    if (result.isValid) {
      setAppliedPromo(result);
    } else {
      alert(result.message);
      setAppliedPromo(null);
    }
  };

  const handleActivatePlan = async () => {
    setIsProcessing(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("User not authenticated");

      const startDate = new Date();
      const expiryDate = new Date();
      expiryDate.setDate(startDate.getDate() + 30);
      const readableExpiry = expiryDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

      const userRef = doc(db, "users", user.uid);
      
      // Data to update in user profile
      const updateData = {
        plan: "profitable",
        subscriptionStartDate: startDate.toISOString(),
        subscriptionExpiryDate: expiryDate.toISOString(),
        email: user.email,
        displayName: user.displayName || "Trader"
      };

      // If a promo was used, add it to the usedPromos array in Firestore
      if (appliedPromo && appliedPromo.code) {
        updateData.usedPromos = arrayUnion(appliedPromo.code);
      }
      
      await setDoc(userRef, updateData, { merge: true });

      await setDoc(doc(db, "transactions", `TXN_${Date.now()}`), {
        uid: user.uid,
        email: user.email,
        amount: finalPrice,
        paymentMode: paymentMethod,
        timestamp: startDate.toISOString(),
        promoUsed: appliedPromo ? appliedPromo.code : "NONE"
      });

      await emailjs.send('service_v7jisfc', 'template_v4n2qjb', {
        user_name: user.displayName || "Trader",
        user_email: user.email,
        plan_name: "Profitable Trader Membership 💎",
        expiry_date: readableExpiry
      });

      setIsSuccess(true);
    } catch (error) {
      alert(`Activation failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        {isSuccess ? (
          <div style={styles.successContent}>
            <div style={styles.successIconWrapper}>
                <CheckCircle2 size={48} color="#10b981" />
            </div>
            <h2 style={styles.successTitle}>Welcome to the Family!</h2>
            <p style={styles.successText}>Your <b>Profitable Trader</b> access is now live. We've sent a confirmation to your email.</p>
            <button onClick={() => window.location.reload()} style={styles.primaryBtn}>Enter Dashboard</button>
          </div>
        ) : (
          <>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.headerTitle}>Upgrade Account</h2>
                <p style={styles.headerSub}>Unlock institutional-grade journaling</p>
              </div>
              <div style={styles.closeIcon} onClick={onClose}><X size={20} /></div>
            </div>

            {/* PLAN PREVIEW CARD */}
            <div style={styles.planCard}>
                <div style={styles.planCardHeader}>
                    <div style={styles.planBadge}><Zap size={12} fill="#38BDF8" /> PREMIUM</div>
                    <span style={styles.planNameText}>{planName}</span>
                </div>
                <div style={styles.featureList}>
                    <div style={styles.featureItem}><Check size={14} color="#10b981"/> Advanced Analytics</div>
                    <div style={styles.featureItem}><Check size={14} color="#10b981"/> PDF Performance Exports</div>
                    <div style={styles.featureItem}><Check size={14} color="#10b981"/> Unlimited Trade Logs</div>
                </div>
            </div>

            <div style={styles.paymentSection}>
                <div style={styles.summaryRow}>
                    <span style={styles.summaryLabel}>Subscription Fee</span>
                    <span style={styles.summaryValue}>₹{planPrice.toLocaleString()}</span>
                </div>
                {appliedPromo && (
                    <div style={styles.summaryRow}>
                        <span style={{...styles.summaryLabel, color: '#10b981'}}>Promo Discount</span>
                        <span style={{...styles.summaryValue, color: '#10b981'}}>-₹{(planPrice - finalPrice).toLocaleString()}</span>
                    </div>
                )}
                <div style={styles.divider} />
                <div style={styles.totalRow}>
                    <span>Total to Pay</span>
                    <span>₹{finalPrice.toLocaleString()}</span>
                </div>
            </div>

            <div style={styles.promoWrapper}>
              <div style={styles.inputContainer}>
                <Ticket size={16} style={styles.inputIcon} />
                <input 
                  placeholder="Have a promo code?" 
                  style={styles.input} 
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                />
              </div>
              <button style={styles.applyBtn} onClick={handleApplyPromo}>Apply</button>
            </div>

            <div style={{ marginTop: 24 }}>
              <p style={styles.labelSection}>Payment Method</p>
              <div style={styles.payGrid}>
                <div 
                  style={{...styles.payOption, borderColor: paymentMethod === 'UPI' ? '#38BDF8' : '#1e293b', background: paymentMethod === 'UPI' ? 'rgba(56, 189, 248, 0.05)' : 'transparent'}} 
                  onClick={() => setPaymentMethod('UPI')}
                >
                  <QrCode size={16} color={paymentMethod === 'UPI' ? '#38BDF8' : '#64748b'} /> UPI / QR
                </div>
                <div 
                  style={{...styles.payOption, borderColor: paymentMethod === 'Card' ? '#38BDF8' : '#1e293b', background: paymentMethod === 'Card' ? 'rgba(56, 189, 248, 0.05)' : 'transparent'}} 
                  onClick={() => setPaymentMethod('Card')}
                >
                  <CreditCard size={16} color={paymentMethod === 'Card' ? '#38BDF8' : '#64748b'} /> Cards
                </div>
              </div>
            </div>

            <button 
              style={styles.primaryBtn} 
              disabled={isProcessing}
              onClick={() => finalPrice === 0 ? handleActivatePlan() : alert("Payment Gateway integrating soon. Use a 100% discount code.")}
            >
              {isProcessing ? <Loader2 className="animate-spin" /> : `Activate Plan — ₹${finalPrice.toLocaleString()}`}
            </button>

            <div style={styles.secureFooter}>
              <ShieldCheck size={14} color="#10b981" /> 
              <span>Secure Encryption</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(2, 6, 23, 0.85)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal: { width: '100%', maxWidth: '440px', background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '28px', padding: '32px', color: 'white', position: 'relative', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  headerTitle: { fontSize: '1.5rem', fontWeight: '800', margin: 0, color: '#f8fafc' },
  headerSub: { fontSize: '0.85rem', color: '#64748b', margin: '4px 0 0 0' },
  closeIcon: { cursor: 'pointer', color: '#475569', transition: 'color 0.2s', ':hover': { color: '#fff' } },
  
  planCard: { background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.07) 0%, rgba(2, 6, 23, 0.5) 100%)', border: '1px solid rgba(56, 189, 248, 0.2)', borderRadius: '20px', padding: '20px', marginBottom: 24 },
  planCardHeader: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 },
  planBadge: { background: 'rgba(56, 189, 248, 0.15)', color: '#38BDF8', fontSize: '9px', fontWeight: '900', padding: '4px 8px', borderRadius: '6px', width: 'fit-content', display: 'flex', alignItems: 'center', gap: 4 },
  planNameText: { fontSize: '1.1rem', fontWeight: '700' },
  featureList: { display: 'flex', flexDirection: 'column', gap: 8 },
  featureItem: { fontSize: '0.8rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 8 },

  paymentSection: { marginBottom: 20 },
  summaryRow: { display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: '0.9rem' },
  summaryLabel: { color: '#64748b' },
  summaryValue: { color: '#f8fafc', fontWeight: '600' },
  divider: { height: '1px', background: 'rgba(255,255,255,0.05)', margin: '12px 0' },
  totalRow: { display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem', fontWeight: '800', color: '#f8fafc' },

  promoWrapper: { display: 'flex', gap: 8, marginTop: 16 },
  inputContainer: { position: 'relative', flex: 1 },
  inputIcon: { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#475569' },
  input: { width: '100%', padding: '12px 12px 12px 40px', background: '#020617', border: '1px solid #1e293b', borderRadius: '12px', color: 'white', outline: 'none', fontSize: '0.9rem', boxSizing: 'border-box' },
  applyBtn: { padding: '0 20px', background: '#fff', color: '#000', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: 'pointer', fontSize: '0.85rem' },
  
  labelSection: { fontSize: '0.75rem', fontWeight: '700', color: '#475569', textTransform: 'uppercase', marginBottom: 12, letterSpacing: '1px' },
  payGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  payOption: { padding: '14px', border: '1px solid #1e293b', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: '0.85rem', cursor: 'pointer', color: '#f8fafc', transition: 'all 0.2s ease' },

  primaryBtn: { width: '100%', padding: '16px', borderRadius: '16px', border: 'none', background: '#38BDF8', color: '#000', fontWeight: '800', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '1rem', marginTop: 28, boxShadow: '0 10px 15px -3px rgba(56, 189, 248, 0.3)' },
  secureFooter: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: '0.75rem', color: '#475569', marginTop: 20 },

  successContent: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '20px 0' },
  successIconWrapper: { width: 80, height: 80, background: 'rgba(16, 185, 129, 0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 },
  successTitle: { fontSize: '1.75rem', fontWeight: '800', margin: '0 0 12px 0' },
  successText: { color: '#94a3b8', lineHeight: '1.6', marginBottom: 32 }
};

export default CheckoutModal;